import { useEffect, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

import type { ParentNode, NodeItem } from '@models/flows';

import PrimaryButton from '@components/PrimaryButton';
import SecondaryButton from '@components/SecondaryButton';
import {
  ErrorNodeConfiguration,
  ConditionalNodeConfiguration,
  TestGroupsNodeConfiguration,
  TransactionNatureNodeConfiguration,
  RecurringActionNodeConfiguration,
  SettingsNodeConfiguration,
  ValueNodeConfiguration,
  FailureNodeConfiguration,
  SuccessNodeConfiguration,
} from '@components/Flows/components/NodeConfigrationNavigation/components';

import { NodeTypes } from '@utils/flows';
import { isNodeType } from '@features/chargerFlows/validation/validation-helpers';

import s from './NodeConfigrationNavigation.module.css';

const NodeTypeTitles: { [type: string]: string } = {
  errorNode: 'Edit Error Node',
  settingsNode: 'Edit Settings Node',
  conditionalNode: 'Edit Conditional Node',
  testGroupsNode: 'Edit Test Groups Node',
  transactionNatureNode: 'Edit Transaction Nature Node',
  recurringActionNode: 'Edit Recurring Action Node',
  valueNode: 'Edit Settings Value Node',
  debtCollectionNode: 'Debt Collection Node',
  failureNode: 'Edit Failure Node',
  successNode: 'Edit Success Node',
};

type Props = {
  isOpen: boolean;
  parentNode: ParentNode | null;
  childNode: NodeItem | null;
  handleSave: (parentNode: ParentNode, childNode: NodeItem) => void;
  handleDelete: (parentNode: ParentNode, childNode: NodeItem) => void;
  handleClose: () => void;
};

function NodeConfigrationNavigation({
  isOpen,
  parentNode,
  childNode,
  handleSave,
  handleDelete,
  handleClose,
}: Props) {
  const [currentChildNode, setCurrentChildNode] = useState<NodeItem | null>(childNode);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleEscapeKey);

    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, handleClose]);

  useEffect(() => {
    setCurrentChildNode(childNode);
  }, [childNode]);

  if (!isOpen) return null;
  if (!currentChildNode) return null;
  if (!parentNode) return null;

  const handleSaveClick = () => {
    if (isNodeType(parentNode.type, NodeTypes.successNode)) {
      const sanitized: NodeItem = { ...currentChildNode };
      delete sanitized.maxRetries;
      handleSave(parentNode, sanitized);
      return;
    }
    handleSave(parentNode, currentChildNode);
  };

  const handleDeleteClick = () => {
    handleDelete(parentNode, currentChildNode);
  };

  const handleNodeChange = (data: NodeItem) => {
    setCurrentChildNode(data);
  };

  const renderConfigurationFields = () => {
    switch (parentNode.type) {
      case NodeTypes.errorNode:
        return (
          <ErrorNodeConfiguration
            currentNode={currentChildNode as NodeItem}
            onChange={handleNodeChange}
          />
        );
      case NodeTypes.settingsNode:
        return (
          <SettingsNodeConfiguration
            currentNode={currentChildNode as NodeItem}
            onChange={handleNodeChange}
          />
        );
      case NodeTypes.conditionalNode:
        return (
          <ConditionalNodeConfiguration
            currentNode={currentChildNode as NodeItem}
            onChange={handleNodeChange}
          />
        );
      case NodeTypes.testGroupsNode:
        return (
          <TestGroupsNodeConfiguration
            parentNode={parentNode}
            currentNode={currentChildNode as NodeItem}
            onChange={handleNodeChange}
          />
        );
      case NodeTypes.transactionNatureNode:
        return (
          <TransactionNatureNodeConfiguration
            currentNode={currentChildNode as NodeItem}
            onChange={handleNodeChange}
          />
        );
      case NodeTypes.recurringActionNode:
        return (
          <RecurringActionNodeConfiguration
            currentNode={currentChildNode as NodeItem}
            onChange={handleNodeChange}
          />
        );
      case NodeTypes.valueNode:
        return (
          <ValueNodeConfiguration
            currentNode={currentChildNode as NodeItem}
            onChange={handleNodeChange}
          />
        );
      case NodeTypes.failureNode:
        return (
          <FailureNodeConfiguration
            currentNode={currentChildNode as NodeItem}
            onChange={handleNodeChange}
          />
        );
      case NodeTypes.successNode:
        return (
          <SuccessNodeConfiguration
            currentNode={currentChildNode as NodeItem}
            onChange={handleNodeChange}
          />
        );
      default:
        return <div>No configuration available for this node type.</div>;
    }
  };

  return (
    <div className={s.configurationNavigation}>
      <div className={s.header}>
        <p className={s.title}>{NodeTypeTitles[parentNode.type] || 'Edit Node'}</p>
        <button type="button" className={s.closeButton} onClick={handleClose}>
          <CloseIcon />
        </button>
      </div>
      <div className={s.content}>
        <div className={s.configurationBlock}>{renderConfigurationFields()}</div>
        <div className={s.actionBlock}>
          <PrimaryButton title={'Save'} icon={<SaveIcon />} onClick={handleSaveClick} />
          {!isNodeType(parentNode.type, NodeTypes.recurringActionNode) && (
            <SecondaryButton title={'Delete'} icon={<DeleteIcon />} onClick={handleDeleteClick} />
          )}
        </div>
      </div>
    </div>
  );
}

export default NodeConfigrationNavigation;
